<!-- Page Header -->
<div class="mb-4">
  <h1 class="h2 mb-1 fw-bold">Chemical Book</h1>
  <p class="text-muted mb-0">Search and reference chemicals for aerial application</p>
</div>

<!-- Tabs -->
<ul class="nav nav-tabs nav-tabs-adn mb-3">
  <li class="nav-item">
    <button
      class="nav-link"
      [class.active]="activeTab === 'epa'"
      (click)="setActiveTab('epa')"
    >
      <i class="bi bi-shield-check me-2"></i>EPA Registered
      <span class="badge bg-secondary ms-2">{{ epaCount() }}</span>
    </button>
  </li>
  <li class="nav-item">
    <button
      class="nav-link"
      [class.active]="activeTab === 'adn-recommended'"
      (click)="setActiveTab('adn-recommended')"
    >
      <i class="bi bi-patch-check-fill me-2 text-adn"></i>Acre Advantage Products
      <span class="badge badge-adn ms-2">{{ adnCount() }}</span>
    </button>
  </li>
</ul>

<!-- ADN Recommended Banner (only show on ADN tab) -->
<div *ngIf="activeTab === 'adn-recommended'" class="adn-banner mb-4">
  <div class="d-flex align-items-center">
    <i class="bi bi-stars me-3 fs-4"></i>
    <div>
      <h6 class="mb-1">Acre Advantage Products</h6>
      <p class="mb-0 small">These products are from our Acre Advantage partners. Use them to earn points on every spray record!</p>
    </div>
  </div>
</div>

<!-- Search and Filters -->
<div class="card mb-4">
  <div class="card-body">
    <div class="row g-3">
      <div class="col-md-8">
        <div class="input-group">
          <span class="input-group-text">
            <i class="bi bi-search"></i>
          </span>
          <input
            type="text"
            class="form-control"
            [placeholder]="activeTab === 'epa' ? 'Search by name, active ingredient, EPA number, target pest, or crop...' : 'Search by name, sponsor, active ingredient, or crop...'"
            [(ngModel)]="searchQuery"
            (ngModelChange)="onSearchChange()"
          />
          <button
            *ngIf="searchQuery"
            class="btn btn-outline-secondary"
            type="button"
            (click)="searchQuery = ''; onSearchChange()"
          >
            <i class="bi bi-x-lg"></i>
          </button>
        </div>
      </div>
      <div class="col-md-4">
        <select
          class="form-select"
          [(ngModel)]="selectedCategory"
          (ngModelChange)="onCategoryChange()"
        >
          <option *ngFor="let cat of categories" [value]="cat.value">
            {{ cat.label }}
          </option>
        </select>
      </div>
    </div>
  </div>
</div>

<!-- Results Summary -->
<div class="d-flex justify-content-between align-items-center mb-3">
  <span class="text-muted">
    <ng-container *ngIf="total() > 0; else noResults">
      Showing {{ (currentPage() - 1) * pageSize + 1 }}-{{ currentPage() * pageSize > total() ? total() : currentPage() * pageSize }} of {{ total() }} {{ activeTab === 'epa' ? 'EPA registered' : 'Acre Advantage' }} chemicals
    </ng-container>
    <ng-template #noResults>
      No chemicals found
    </ng-template>
  </span>
  <span *ngIf="isSearching()" class="text-muted">
    <span class="spinner-border spinner-border-sm me-1" role="status"></span>
    Searching...
  </span>
</div>

<!-- Reference Guide (collapsible) -->
<div class="card mb-3">
  <button type="button" class="card-header p-3 w-100 border-0 bg-transparent text-start"
    (click)="isReferenceGuideOpen = !isReferenceGuideOpen"
    [attr.aria-expanded]="isReferenceGuideOpen"
    aria-controls="referenceGuideBody">
    <div class="d-flex justify-content-between align-items-center">
      <h6 class="mb-0"><i class="bi bi-book me-2"></i>Reference Guide</h6>
      <i class="bi" [ngClass]="isReferenceGuideOpen ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
    </div>
  </button>
  <div class="card-body" id="referenceGuideBody" [class.d-none]="!isReferenceGuideOpen">
    <div class="row g-4">
      <div class="col-md-6">
        <h6 class="small fw-bold text-muted mb-2">Signal Words</h6>
        <div class="d-flex flex-column gap-2">
          <div class="d-flex align-items-center gap-2">
            <span class="signal-word signal-caution">CAUTION</span>
            <span class="small text-muted">- Slightly toxic</span>
          </div>
          <div class="d-flex align-items-center gap-2">
            <span class="signal-word signal-warning">WARNING</span>
            <span class="small text-muted">- Moderately toxic</span>
          </div>
          <div class="d-flex align-items-center gap-2">
            <span class="signal-word signal-danger">DANGER</span>
            <span class="small text-muted">- Highly toxic</span>
          </div>
        </div>
      </div>
      <div class="col-md-6">
        <h6 class="small fw-bold text-muted mb-2">Categories</h6>
        <div class="d-flex flex-wrap gap-2">
          <span class="badge badge-herbicide">Herbicide</span>
          <span class="badge badge-insecticide">Insecticide</span>
          <span class="badge badge-fungicide">Fungicide</span>
          <span class="badge badge-fertilizer">Fertilizer</span>
          <span class="badge badge-adjuvant">Adjuvant</span>
          <span class="badge badge-igr">IGR</span>
          <span class="badge badge-miticide">Miticide</span>
          <span class="badge badge-pgr">PGR</span>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Results Table -->
<div class="card">
  <div class="card-body p-0">
    <ng-container *ngIf="!isLoading() && filteredChemicals.length > 0; else emptyState">
      <div class="table-responsive">
        <!-- EPA Chemicals Table -->
        <table *ngIf="activeTab === 'epa'" class="table table-hover mb-0">
          <thead>
            <tr>
              <th class="ps-3">Chemical Name</th>
              <th>Alternate Names</th>
              <th>EPA #</th>
              <th>Active Ingredient</th>
              <th>Category</th>
              <th>Signal Word</th>
              <th class="text-end pe-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let chemical of filteredChemicals" class="chemical-row">
              <td class="ps-3">
                <div class="d-flex flex-column">
                  <button class="btn btn-link p-0 text-start fw-medium text-decoration-none chemical-name-link"
                          (click)="openDetailsModal(chemical)"
                          [innerHTML]="chemical.name | highlight:searchQuery">
                  </button>
                  <span class="text-muted small" [innerHTML]="chemical.manufacturer | highlight:searchQuery"></span>
                </div>
              </td>
              <td>
                <div class="d-flex flex-column gap-1">
                  <span *ngIf="chemical.displayName && chemical.displayName !== chemical.name" class="small fw-medium text-primary"
                        [innerHTML]="chemical.displayName | highlight:searchQuery">
                  </span>
                  <span *ngIf="chemical.epaAltBrandNames" class="small text-muted" [title]="chemical.epaAltBrandNames"
                        [innerHTML]="getAltNamesPreview(chemical.epaAltBrandNames) | highlight:searchQuery">
                  </span>
                  <span *ngIf="!chemical.displayName || chemical.displayName === chemical.name" class="small text-muted">
                    <ng-container *ngIf="!chemical.epaAltBrandNames">—</ng-container>
                  </span>
                </div>
              </td>
              <td>
                <span class="epa-number-badge" [innerHTML]="chemical.epaNumber | highlight:searchQuery"></span>
              </td>
              <td>
                <span class="small" [innerHTML]="chemical.activeIngredient | highlight:searchQuery"></span>
              </td>
              <td>
                <span class="badge" [ngClass]="getCategoryBadgeClass(chemical.category)">
                  {{ getCategoryLabel(chemical.category) }}
                </span>
              </td>
              <td>
                <span class="signal-word" [ngClass]="getSignalWordClass(chemical.signalWord)">
                  {{ chemical.signalWord }}
                </span>
              </td>
              <td class="text-end pe-3">
                <div class="d-flex gap-2 justify-content-end">
                  <button
                    class="btn btn-sm btn-outline-secondary"
                    (click)="openDetailsModal(chemical)"
                    title="View details"
                  >
                    <i class="bi bi-info-circle"></i>
                  </button>
                  <!-- Single label button -->
                  <button
                    *ngIf="!(chemical.currentLabels && chemical.currentLabels.length > 1)"
                    class="btn btn-sm btn-outline-primary"
                    (click)="viewLabel(chemical)"
                    [disabled]="!(chemical.currentLabels?.length || chemical.currentLabelUrl)"
                    [attr.aria-label]="(chemical.currentLabels?.length || chemical.currentLabelUrl) ? 'View label PDF' : 'No label available'"
                    [title]="(chemical.currentLabels?.length || chemical.currentLabelUrl) ? 'View label PDF' : 'No label available'"
                  >
                    <i class="bi bi-file-pdf"></i>
                  </button>
                  <!-- Multi-label dropdown -->
                  <div *ngIf="chemical.currentLabels && chemical.currentLabels.length > 1" class="btn-group">
                    <button type="button" class="btn btn-sm btn-outline-primary dropdown-toggle"
                            data-bs-toggle="dropdown" aria-expanded="false" aria-label="View labels" title="View labels">
                      <i class="bi bi-file-pdf me-1"></i>Labels
                    </button>
                    <ul class="dropdown-menu dropdown-menu-end">
                      <li *ngFor="let label of chemical.currentLabels">
                        <button type="button" class="dropdown-item" (click)="viewLabel(chemical, label.url)">
                          <i class="bi bi-file-earmark-pdf me-2"></i>{{ label.name || 'Label PDF' }}
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- ADN Recommended Chemicals Table -->
        <table *ngIf="activeTab === 'adn-recommended'" class="table table-hover mb-0">
          <thead>
            <tr>
              <th class="ps-3">Product</th>
              <th>Sponsor</th>
              <th>Active Ingredient</th>
              <th>Category</th>
              <th>Signal Word</th>
              <th class="text-end pe-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let chemical of filteredChemicals" class="chemical-row" [class.featured-row]="chemical.isFeatured">
              <td class="ps-3">
                <div class="d-flex flex-column">
                  <div class="d-flex align-items-center gap-2">
                    <button class="btn btn-link p-0 text-start fw-medium text-decoration-none chemical-name-link"
                            (click)="openDetailsModal(chemical)">
                      {{ chemical.name }}
                    </button>
                    <span *ngIf="chemical.isFeatured" class="badge badge-featured">
                      <i class="bi bi-star-fill me-1"></i>Featured
                    </span>
                  </div>
                  <span class="text-muted small">{{ chemical.manufacturer }}</span>
                  <span *ngIf="chemical.specialOffer" class="discount-badge mt-1">
                    <i class="bi bi-tag-fill me-1"></i>{{ chemical.specialOffer }}
                  </span>
                </div>
              </td>
              <td>
                <div class="sponsor-cell">
                  <button class="btn btn-link btn-sm p-0 text-decoration-none" (click)="visitSponsor(chemical)"
                          *ngIf="chemical.sponsorName">
                    <span class="sponsor-name">{{ chemical.sponsorName }}</span>
                    <i class="bi bi-box-arrow-up-right ms-1 small"></i>
                  </button>
                </div>
              </td>
              <td>
                <span class="small">{{ chemical.activeIngredient }}</span>
              </td>
              <td>
                <span class="badge" [ngClass]="getCategoryBadgeClass(chemical.category)">
                  {{ getCategoryLabel(chemical.category) }}
                </span>
              </td>
              <td>
                <span class="signal-word" [ngClass]="getSignalWordClass(chemical.signalWord)">
                  {{ chemical.signalWord }}
                </span>
              </td>
              <td class="text-end pe-3">
                <div class="d-flex gap-2 justify-content-end flex-wrap">
                  <button class="btn btn-sm btn-outline-secondary" (click)="openDetailsModal(chemical)"
                          title="View details">
                    <i class="bi bi-info-circle"></i>
                  </button>
                  <!-- Single label button -->
                  <button *ngIf="!(chemical.currentLabels && chemical.currentLabels.length > 1)"
                          class="btn btn-sm btn-outline-primary" (click)="viewLabel(chemical)"
                          [disabled]="!(chemical.currentLabels?.length || chemical.currentLabelUrl)"
                          aria-label="View label PDF" title="View Label">
                    <i class="bi bi-file-pdf"></i>
                  </button>
                  <!-- Multi-label dropdown -->
                  <div *ngIf="chemical.currentLabels && chemical.currentLabels.length > 1" class="btn-group">
                    <button type="button" class="btn btn-sm btn-outline-primary dropdown-toggle"
                            data-bs-toggle="dropdown" aria-expanded="false" aria-label="View labels" title="View labels">
                      <i class="bi bi-file-pdf me-1"></i>Labels
                    </button>
                    <ul class="dropdown-menu dropdown-menu-end">
                      <li *ngFor="let label of chemical.currentLabels">
                        <button type="button" class="dropdown-item" (click)="viewLabel(chemical, label.url)">
                          <i class="bi bi-file-earmark-pdf me-2"></i>{{ label.name || 'Label PDF' }}
                        </button>
                      </li>
                    </ul>
                  </div>
                  <button class="btn btn-sm btn-adn" (click)="openProductDetails(chemical)" *ngIf="chemical.sponsorName">
                    <i class="bi bi-box-arrow-up-right me-1"></i>Product Details
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="card-footer d-flex justify-content-between align-items-center" *ngIf="totalPages() > 1">
        <div class="text-muted small">
          Page {{ currentPage() }} of {{ totalPages() }}
        </div>
        <nav aria-label="Chemical book pagination">
          <ul class="pagination pagination-sm mb-0">
            <!-- First page -->
            <li class="page-item" [class.disabled]="currentPage() === 1">
              <button class="page-link" (click)="goToPage(1)" [disabled]="currentPage() === 1" title="First page">
                <i class="bi bi-chevron-double-left"></i>
              </button>
            </li>
            <!-- Previous page -->
            <li class="page-item" [class.disabled]="currentPage() === 1">
              <button class="page-link" (click)="previousPage()" [disabled]="currentPage() === 1">
                <i class="bi bi-chevron-left"></i>
              </button>
            </li>
            <!-- Page numbers -->
            <li class="page-item" *ngFor="let page of getPageNumbers()" [class.active]="page === currentPage()">
              <button class="page-link" (click)="goToPage(page)">{{ page }}</button>
            </li>
            <!-- Next page -->
            <li class="page-item" [class.disabled]="currentPage() === totalPages()">
              <button class="page-link" (click)="nextPage()" [disabled]="currentPage() === totalPages()">
                <i class="bi bi-chevron-right"></i>
              </button>
            </li>
            <!-- Last page -->
            <li class="page-item" [class.disabled]="currentPage() === totalPages()">
              <button class="page-link" (click)="goToPage(totalPages())" [disabled]="currentPage() === totalPages()" title="Last page">
                <i class="bi bi-chevron-double-right"></i>
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </ng-container>

    <ng-template #emptyState>
      <div class="p-5 text-center">
        <div class="empty-state-icon mb-3">
          <i class="bi bi-search"></i>
        </div>
        <h5>No Chemicals Found</h5>
        <p class="text-muted mb-0">
          <ng-container *ngIf="searchQuery || selectedCategory; else noData">
            Try adjusting your search or filter criteria.
          </ng-container>
          <ng-template #noData>
            No chemicals are currently available in the database.
          </ng-template>
        </p>
      </div>
    </ng-template>
  </div>
</div>

<!-- Chemical Details Modal -->
<div class="modal-backdrop fade show" *ngIf="showDetailsModal"></div>
<div class="modal fade show d-block" tabindex="-1" *ngIf="showDetailsModal" (click)="closeDetailsModal()">
  <div class="modal-dialog modal-dialog-centered modal-lg" (click)="$event.stopPropagation()">
    <div class="modal-content chemical-details-modal">
      <div class="modal-header">
        <h5 class="modal-title">
          <i class="bi bi-info-circle me-2 text-primary"></i>Chemical Details
        </h5>
        <button type="button" class="btn-close" (click)="closeDetailsModal()"></button>
      </div>
      <div class="modal-body" *ngIf="selectedChemical">
        <!-- Header with name and badges -->
        <div class="pb-3 mb-3 border-bottom-outline">
          <h4 class="mb-1">{{ selectedChemical.name }}</h4>
          <p class="text-muted mb-2">{{ selectedChemical.manufacturer }}</p>
          <div class="d-flex flex-wrap gap-2 align-items-center">
            <span class="badge" [ngClass]="getCategoryBadgeClass(selectedChemical.category)">
              {{ getCategoryLabel(selectedChemical.category) }}
            </span>
            <span class="signal-word" [ngClass]="getSignalWordClass(selectedChemical.signalWord)">
              {{ selectedChemical.signalWord }}
            </span>
            <span *ngIf="selectedChemical.source === 'epa'" class="badge badge-epa">EPA Registered</span>
            <span *ngIf="selectedChemical.source === 'adn-recommended'" class="badge badge-adn">Acre Advantage</span>
            <span *ngIf="selectedChemical.isFeatured" class="badge badge-featured">
              <i class="bi bi-star-fill me-1"></i>Featured
            </span>
          </div>
        </div>

        <!-- Sponsor Info (ADN Recommended only) - shown near top -->
        <div class="detail-card mb-3" *ngIf="selectedChemical.source === 'adn-recommended' && selectedChemical.sponsorName">
          <h6 class="detail-card-title">
            <i class="bi bi-building me-2"></i>Sponsor Information
          </h6>
          <div class="d-flex align-items-start gap-3">
            <img *ngIf="selectedChemical.sponsorLogoUrl"
                 [src]="getLogoFullUrl(selectedChemical.sponsorLogoUrl)"
                 [alt]="selectedChemical.sponsorName"
                 class="sponsor-logo-preview"
                 [style.background-color]="selectedChemical.sponsorBrandColor || null"
                 [style.border-color]="selectedChemical.sponsorBrandColor ? 'transparent' : null">
            <div class="flex-grow-1">
              <h6 class="mb-2">{{ selectedChemical.sponsorName }}</h6>
              <div class="d-flex flex-column gap-1 small">
                <div *ngIf="selectedChemical.sponsorPhone">
                  <i class="bi bi-telephone me-2 text-muted"></i>
                  <a [href]="'tel:' + selectedChemical.sponsorPhone" class="text-decoration-none">
                    {{ selectedChemical.sponsorPhone | phoneFormat }}
                  </a>
                </div>
                <div *ngIf="selectedChemical.sponsorEmail">
                  <i class="bi bi-envelope me-2 text-muted"></i>
                  <a [href]="'mailto:' + selectedChemical.sponsorEmail" class="text-decoration-none">
                    {{ selectedChemical.sponsorEmail }}
                  </a>
                </div>
                <div *ngIf="selectedChemical.sponsorWebsite">
                  <i class="bi bi-globe me-2 text-muted"></i>
                  <a [href]="selectedChemical.sponsorWebsite" target="_blank" class="text-decoration-none">
                    Visit Website <i class="bi bi-box-arrow-up-right ms-1"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div *ngIf="selectedChemical.specialOffer" class="discount-badge-lg mt-3">
            <i class="bi bi-tag-fill me-2"></i>{{ selectedChemical.specialOffer }}
          </div>
        </div>

        <!-- Details Grid -->
        <div class="row g-3">
          <!-- Left Column -->
          <div class="col-md-6">
            <div class="detail-card">
              <h6 class="detail-card-title">
                <i class="bi bi-card-text me-2"></i>Basic Information
              </h6>
              <dl class="detail-list mb-0">
                <div class="detail-item" *ngIf="selectedChemical.epaNumber">
                  <dt>EPA Number</dt>
                  <dd><span class="epa-number-badge">{{ selectedChemical.epaNumber }}</span></dd>
                </div>
                <div class="detail-item">
                  <dt>Active Ingredient</dt>
                  <dd>{{ selectedChemical.activeIngredient || '—' }}</dd>
                </div>
                <div class="detail-item">
                  <dt>Formulation</dt>
                  <dd>{{ selectedChemical.formulation || '—' }}</dd>
                </div>
                <div class="detail-item" *ngIf="selectedChemical.displayName && selectedChemical.displayName !== selectedChemical.name">
                  <dt>Display Name</dt>
                  <dd>{{ selectedChemical.displayName }}</dd>
                </div>
                <div class="detail-item" *ngIf="selectedChemical.epaAltBrandNames">
                  <dt>Alternate Names</dt>
                  <dd class="small">{{ selectedChemical.epaAltBrandNames }}</dd>
                </div>
                <div class="detail-item" *ngIf="selectedChemical.sdsUrl">
                  <dt>Safety Data Sheet</dt>
                  <dd><a [href]="selectedChemical.sdsUrl" target="_blank" rel="noopener noreferrer" class="text-decoration-none"><i class="bi bi-shield-check me-1"></i>View SDS</a></dd>
                </div>
                <div class="detail-item" *ngIf="selectedChemical.productUrl">
                  <dt>Product Page</dt>
                  <dd><a [href]="selectedChemical.productUrl" target="_blank" rel="noopener noreferrer" class="text-decoration-none"><i class="bi bi-box-arrow-up-right me-1"></i>View Product</a></dd>
                </div>
              </dl>
            </div>
          </div>

          <!-- Right Column -->
          <div class="col-md-6">
            <div class="detail-card">
              <h6 class="detail-card-title">
                <i class="bi bi-clock-history me-2"></i>Intervals & Rates
              </h6>
              <dl class="detail-list mb-0">
                <div class="detail-item">
                  <dt>REI (Restricted Entry Interval)</dt>
                  <dd>{{ selectedChemical.rei || '—' }}</dd>
                </div>
                <div class="detail-item">
                  <dt>PHI (Pre-Harvest Interval)</dt>
                  <dd>{{ selectedChemical.phi || '—' }}</dd>
                </div>
                <div class="detail-item">
                  <dt>Default Rate</dt>
                  <dd>{{ selectedChemical.defaultRate || '—' }}</dd>
                </div>
              </dl>
            </div>
          </div>

          <!-- Target Pests -->
          <div class="col-12" *ngIf="selectedChemical.targetPests && selectedChemical.targetPests.length > 0">
            <div class="detail-card">
              <h6 class="detail-card-title">
                <i class="bi bi-bug me-2"></i>Target Pests
                <span class="badge bg-secondary ms-2">{{ selectedChemical.targetPests.length }}</span>
              </h6>
              <div class="d-flex flex-wrap gap-2 badge-scroll-container">
                <span *ngFor="let pest of selectedChemical.targetPests" class="badge bg-warning-subtle text-warning-emphasis">
                  {{ pest }}
                </span>
              </div>
            </div>
          </div>

          <!-- Crops -->
          <div class="col-12" *ngIf="selectedChemical.crops && selectedChemical.crops.length > 0">
            <div class="detail-card">
              <h6 class="detail-card-title">
                <i class="bi bi-tree me-2"></i>Approved Crops
                <span class="badge bg-secondary ms-2">{{ selectedChemical.crops.length }}</span>
              </h6>
              <div class="d-flex flex-wrap gap-2 badge-scroll-container">
                <span *ngFor="let crop of selectedChemical.crops" class="badge bg-success-subtle text-success">
                  {{ crop }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" (click)="closeDetailsModal()">Close</button>
        <!-- SDS link -->
        <a *ngIf="selectedChemical?.sdsUrl"
           [href]="selectedChemical!.sdsUrl"
           target="_blank"
           rel="noopener noreferrer"
           class="btn btn-outline-info">
          <i class="bi bi-shield-check me-2"></i>SDS
        </a>
        <!-- Product URL link -->
        <a *ngIf="selectedChemical?.productUrl"
           [href]="selectedChemical!.productUrl"
           target="_blank"
           rel="noopener noreferrer"
           class="btn btn-outline-secondary">
          <i class="bi bi-box-arrow-up-right me-2"></i>Product Page
        </a>
        <!-- Single label button -->
        <button *ngIf="selectedChemical && (selectedChemical.currentLabels?.length || selectedChemical.currentLabelUrl) && !(selectedChemical.currentLabels && selectedChemical.currentLabels.length > 1)"
                type="button"
                class="btn btn-primary"
                (click)="viewLabel(selectedChemical!)">
          <i class="bi bi-file-pdf me-2"></i>View Label
        </button>
        <!-- Multi-label dropdown -->
        <div *ngIf="selectedChemical?.currentLabels && selectedChemical!.currentLabels!.length > 1" class="btn-group dropup">
          <button type="button" class="btn btn-primary dropdown-toggle"
                  data-bs-toggle="dropdown" aria-expanded="false">
            <i class="bi bi-file-pdf me-2"></i>View Labels
          </button>
          <ul class="dropdown-menu">
            <li *ngFor="let label of selectedChemical!.currentLabels">
              <button type="button" class="dropdown-item" (click)="viewLabel(selectedChemical!, label.url)">
                <i class="bi bi-file-earmark-pdf me-2"></i>{{ label.name || 'Label PDF' }}
              </button>
            </li>
          </ul>
        </div>
        <button *ngIf="selectedChemical?.source === 'adn-recommended' && selectedChemical?.sponsorName"
                type="button"
                class="btn btn-adn"
                (click)="openProductDetails(selectedChemical!); closeDetailsModal()">
          <i class="bi bi-box-arrow-up-right me-2"></i>Product Details
        </button>
      </div>
    </div>
  </div>
</div>

<!-- How to Buy Modal -->
<app-how-to-buy-modal
  [sponsorId]="selectedSponsorId || ''"
  [visible]="showHowToBuyModal"
  (close)="closeHowToBuyModal()">
</app-how-to-buy-modal>
